import React, {useState} from "react";
import {
  Divider,
  InputAdornment,
  ListSubheader,
  TextField,
} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import CheckIcon from "@material-ui/icons/Check";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import PublicIcon from "@material-ui/icons/Public";
import {ChecklistOptions} from "modules/projects/actions/checklist_options_fetch";
import {
  buildChecklistOptions,
  ChecklistOptionItem,
  filterChecklistOptions,
} from "./checklist_options";

const SECTION_LABELS: Record<string, string | undefined> = {
  recent: "Recently used",
};

const styles = {
  groupHeader: {
    font: "500 11px/28px Roboto, sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#757575",
    padding: "0 16px",
    background: "#fff",
  },
  groupList: {padding: 0},
  divider: {margin: "8px 0"},
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  defaultIcon: {fontSize: 18, color: "#757575"},
  optionName: {
    flex: 1,
    minWidth: 0,
    fontSize: "14.5px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
} as const;

interface RegionChecklistSelectorProps {
  options?: ChecklistOptions;
  value: number | null;
  onChange: (issuesetId: number | null) => void;
  helperText?: string;
  disabled?: boolean;
  error?: boolean;
  size?: "small" | "medium";
}

function renderHighlightedName(name: string, inputValue: string) {
  const query = inputValue.trim();
  const index = query ? name.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (index === -1) {
    return name;
  }
  return (
    <>
      {name.slice(0, index)}
      <b>{name.slice(index, index + query.length)}</b>
      {name.slice(index + query.length)}
    </>
  );
}

const RegionChecklistSelector: React.FC<RegionChecklistSelectorProps> = ({
  options,
  value,
  onChange,
  helperText,
  disabled,
  error,
  size = "small",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const items = options ? buildChecklistOptions(options) : [];
  const selectedItem =
    value === null
      ? null
      : items.find(item => item.id === value) ?? {
          id: value,
          name: "Unknown checklist",
          section: "all" as const,
        };
  const isLabelShrunk = isFocused || Boolean(selectedItem);
  const query = inputValue === selectedItem?.name ? "" : inputValue;
  const isDefault = (item: ChecklistOptionItem) =>
    item.id === options?.default_issueset_id;

  return (
    <Autocomplete<ChecklistOptionItem>
      size={size}
      fullWidth
      disabled={disabled}
      loading={!options}
      options={items}
      value={selectedItem}
      onChange={(_, item) => onChange(item ? item.id : null)}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={item => item.name}
      getOptionSelected={(item, selected) => item.id === selected.id}
      filterOptions={(list, state) =>
        filterChecklistOptions(list, state.inputValue)
      }
      groupBy={item => item.section}
      renderGroup={params => (
        <li key={params.key}>
          {String(params.key) !== "0" && <Divider style={styles.divider} />}
          {SECTION_LABELS[params.group] && (
            <ListSubheader
              component="div"
              disableSticky
              style={styles.groupHeader}
            >
              {SECTION_LABELS[params.group]}
            </ListSubheader>
          )}
          <ul style={styles.groupList}>{params.children}</ul>
        </li>
      )}
      renderOption={(item, {selected}) => (
        <div style={styles.option}>
          <span style={styles.optionName}>
            {renderHighlightedName(item.name, query)}
          </span>
          <CheckIcon
            color="primary"
            fontSize="small"
            style={{visibility: selected ? "visible" : "hidden"}}
          />
          <PublicIcon
            titleAccess={isDefault(item) ? "Project default" : undefined}
            style={{
              ...styles.defaultIcon,
              visibility: isDefault(item) ? "visible" : "hidden",
            }}
          />
        </div>
      )}
      noOptionsText={`No checklists match "${query.trim()}"`}
      ListboxProps={{style: {maxHeight: 296}}}
      renderInput={params => (
        <TextField
          {...params}
          label="Region checklist"
          placeholder="Search checklists"
          variant="outlined"
          helperText={helperText}
          error={error}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: isLabelShrunk,
            style: isLabelShrunk
              ? undefined
              : {
                  transform: `translate(46px, ${
                    size === "small" ? 12 : 20
                  }px) scale(1)`,
                },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <PlaylistAddCheckIcon
                    color={isLabelShrunk ? "primary" : "action"}
                  />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default RegionChecklistSelector;
